import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import Toast from 'react-native-toast-message';
import { useDispatch, useSelector } from 'react-redux';

import {
	BronzeButton,
	BronzeTournamentCard,
	GoldButton,
	GoldTournamentCard,
	SilverTournamentCard,
	WinButton,
} from '../../../assets';
import { RootState } from '../../../store';
import { selectIsLogged } from '../../../store/auth/authReducer';
import {
	tournamentsListRefreshRequest,
	tournamentsSubscribe,
	tournamentsUnregisterRequest,
	tournamentsUnsubscribe,
} from '../../../store/tournaments/tournamentsActions';
import {
	selectTournaments,
	selectTournamentsListLoading,
} from '../../../store/tournaments/tournamentsReducer';
import { useDevice } from '../../../utils/hooks/useDevice';
import { useSafeViewport } from '../../../utils/hooks/useSafeViewport';
import { RootStackParamList } from '../../navigator';
import { useHeaderTopOffset } from '../../Pages/headerOffset';
import { getNavRowHeight } from '../AppFooter/Nav/FooterNavButtonsV2';
import { CarouselNav } from '../common/CarouselNav';
import { getDesktopColumns } from '../Desktop/DesktopBottomBars';
import { DesktopCoverflow } from '../Desktop/DesktopCoverflow';
import { DesktopJackpotBar } from '../Desktop/DesktopJackpotBar';
import { useDesktopLayout } from '../Desktop/desktopLayout';
import { DesktopRecentTablesBar } from '../Desktop/DesktopRecentTablesBar';
import { HEADER_BAR } from '../Top/headerBar';
import RegisteredTournamentsPanel from '../Tournaments/RegisteredTournamentsPanel';
import TournamentInfoModal from '../Tournaments/TournamentInfoModal';
import { tournamentTitle } from '../Tournaments/tournamentModalTheme';
import {
	TournamentsCarousel,
	TournamentsCarouselHandle,
	TournamentSlide,
} from './TournamentsCarousel';

export const TROPHIES: Record<string, any> = {
	bronze: BronzeTournamentCard,
	silver: SilverTournamentCard,
	gold: GoldTournamentCard,
};
export const WIN_BUTTONS: Record<string, any> = {
	bronze: BronzeButton,
	silver: WinButton,
	gold: GoldButton,
};

export type SnG = {
	id: number;
	entry_icon: 'bronze' | 'silver' | 'gold';
	entry_level: number;
	winAmount: string;
	entry_size: string;
	users: string[];
	active_tables: number;
	players: number;
	prices: { [key: number]: number };
	registered?: boolean;
	roomId: string;
	start_in: Date | null;
	close_at: Date | null;
	tournament_chips: number;
};

export type TournamentProps = {
	sng: SnG;
	trophySource: any;
	winButtonSource: any;
	index: number;
	totalCount: number;
	isSpecial?: boolean;
	itemWidth: number;
	itemHeight?: number;
	showNoUserWarning?: boolean;
	showLevelLockWarning?: boolean;
	loginWarningText?: string;
};

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
// Mobile carousel geometry, taken from the Figma frame (390pt wide, 226x416 centre card). These
// match the lobby city carousel, which the tournament carousel is a reskin of.
const DESIGN_FRAME_WIDTH = 390;
const DESIGN_CARD_WIDTH = 226;
const DESIGN_CARD_HEIGHT = 416;
const PORTRAIT_CARD_ASPECT = DESIGN_CARD_HEIGHT / DESIGN_CARD_WIDTH;
const NAV_BLOCK_HEIGHT = 34;
const NAV_TOP_GAP = 12;
// "Registered Tournaments" (2747:16906) sits 11 under the carousel dots, 361 wide against the
// design's 226-wide card.
const DESIGN_PANEL_WIDTH = 361;
const PANEL_TOP_GAP = 11;
// The column keeps this far off the header bar above and the Home tab below (the tab stands above
// the nav bar). AppFooter keeps the nav 12 design units off the bottom.
const PANEL_HOME_GAP = 10;
const FOOTER_BOTTOM_GAP = 12;
// The panel is always three rows tall (36 each, 6 apart) under its 65 of title, rule and padding;
// more rows scroll inside it, fewer leave it empty below.
const PANEL_VISIBLE_ROWS = 3;
const PANEL_LIST_HEIGHT = PANEL_VISIBLE_ROWS * 36 + (PANEL_VISIBLE_ROWS - 1) * 6;
const PANEL_DESIGN_HEIGHT = 65 + PANEL_LIST_HEIGHT;
/** The panel's empty line, which the section's budget already counts. */
const PANEL_EMPTY_LINE = 32;
// The desktop page is the VIP rooms' (VipRoomsScreen): the desktop home's 592-high block, the
// bottom bars at its 510, the cards 420 high from 8 down. The registered panel stands beside the
// front card at its height, its rows scrolling inside it.
const DESKTOP_BLOCK_HEIGHT = 592;
const DESKTOP_BARS_TOP = 510;
const DESKTOP_CARD_HEIGHT = 420;
const DESKTOP_TOP_GAP = 8;

type Nav = NativeStackNavigationProp<RootStackParamList>;

export const TournamentsSection = () => {
	const dispatch = useDispatch();
	const navigation = useNavigation<Nav>();
	const authToken = useSelector((state: RootState) => state.auth?.token ?? null);
	const authUserId = useSelector((state: RootState) => state.auth?.user?.id ?? null);
	const isLogged = useSelector(selectIsLogged);
	const isAuthIdentityPending = Boolean(authToken) && !authUserId;

	useEffect(() => {
		if (isAuthIdentityPending) {
			dispatch(tournamentsUnsubscribe());
			return;
		}

		dispatch(tournamentsSubscribe());

		return () => {
			dispatch(tournamentsUnsubscribe());
		};
	}, [dispatch, isAuthIdentityPending, authUserId, isLogged]);

	const tournaments = useSelector(selectTournaments);
	const loading = useSelector(selectTournamentsListLoading);
	const [initialLoadDone, setInitialLoadDone] = useState(false);
	useEffect(() => {
		if (loading) setInitialLoadDone(true);
	}, [loading]);
	const { safeAreaWidth, safeAreaHeight, isXs, isDesktop } = useDevice();
	const { insets } = useSafeViewport();
	const headerTopOffset = useHeaderTopOffset();
	const desktop = useDesktopLayout();
	const carouselRef = useRef<TournamentsCarouselHandle>(null);
	const [activeIndex, setActiveIndex] = useState(0);
	const carouselProgress = useSharedValue(0);
	// The tournament whose info modal is open - from a card tap or a registered row. Kept by id so
	// the modal follows the live list item as registrations change.
	const [infoTournamentId, setInfoTournamentId] = useState<string | null>(null);
	const openTournament = useCallback((sng: SnG) => setInfoTournamentId(String(sng.id)), []);
	const unregisterTournament = useCallback(
		(sng: SnG) => {
			dispatch(tournamentsUnregisterRequest({ tournamentId: String(sng.id) }));
			Toast.show({
				type: 'success',
				text1: t('T_TOURNAMENT_UNREGISTER'),
				visibilityTime: 3200,
				position: 'bottom',
			});
		},
		[dispatch],
	);

	// The poll reads the latest list from a ref so socket-driven list updates
	// (new array identity on every getSnGLobbyUpdate) don't tear down and
	// recreate the interval.
	const tournamentsRef = useRef(tournaments);
	useEffect(() => {
		tournamentsRef.current = tournaments;
	}, [tournaments]);

	useEffect(() => {
		const interval = setInterval(() => {
			const current = tournamentsRef.current;
			if (!current.length) return;
			const hasExpired = current.some(s => {
				const closeAt = (s as SnG).close_at;
				return closeAt != null && new Date(closeAt).getTime() <= Date.now();
			});
			if (hasExpired) {
				dispatch(tournamentsListRefreshRequest());
			}
		}, 5000);
		return () => clearInterval(interval);
	}, [dispatch]);

	const normalizedTournaments = useMemo(
		() =>
			tournaments
				.map((sng: SnG) => ({
					...sng,
					winAmount: Array.isArray(sng.prices) ? sng.prices[1] : (sng.prices?.['1'] ?? 0),
				}))
				.sort((a: SnG, b: SnG) => +a.entry_size - +b.entry_size),
		[tournaments],
	);

	const presentation = useMemo(
		() =>
			normalizedTournaments.map((sng: SnG, idx: number) => {
				const trophyKey = sng.entry_icon;
				return {
					sng,
					id: String(sng.id ?? idx),
					trophyKey,
					winButtonKey: trophyKey,
					activeTables: sng.active_tables,
					winAmount: Array.isArray(sng.prices) ? sng.prices[1] : sng.prices?.['1'],
				};
			}),
		[normalizedTournaments],
	);

	// "My registered tournaments": the saga flags each list item the player is in.
	const registeredTournaments = useMemo(
		() => normalizedTournaments.filter((sng: SnG) => sng.registered),
		[normalizedTournaments],
	);
	const infoTournament = useMemo(
		() =>
			infoTournamentId == null
				? null
				: (normalizedTournaments.find((sng: SnG) => String(sng.id) === infoTournamentId) ?? null),
		[infoTournamentId, normalizedTournaments],
	);

	const topReserve = safeAreaHeight * 0.1;
	const bottomReserve = isXs ? safeAreaWidth * 0.2 : safeAreaHeight * 0.18;
	const middleHeight = Math.max(safeAreaHeight - topReserve - bottomReserve, safeAreaHeight * 0.52);
	const sectionHeight = Math.round(clamp(middleHeight, 440, safeAreaHeight * 0.76));

	// The mobile wheel shows one featured card with its neighbours peeking in.
	const navReserveScale = clamp(safeAreaWidth / DESIGN_FRAME_WIDTH, 1, 1.6);
	const navHeight = (NAV_BLOCK_HEIGHT + NAV_TOP_GAP) * navReserveScale;
	// The empty panel's 32-tall line is part of the layout's budget; the rest of the fixed
	// three-row list comes out of the wheel, so the card shrinks instead of the column spilling
	// under the header on shorter phones.
	const panelExtra = (PANEL_LIST_HEIGHT - PANEL_EMPTY_LINE) * (safeAreaWidth / DESIGN_FRAME_WIDTH);
	const wheelHeight = Math.max(sectionHeight - navHeight - panelExtra, sectionHeight * 0.55);
	// The panel keeps the width the card would have without registered rows - only the wheel gives.
	const baseCardWidth = Math.min(
		safeAreaWidth * (DESIGN_CARD_WIDTH / DESIGN_FRAME_WIDTH),
		(Math.max(sectionHeight - navHeight, sectionHeight * 0.7) * 0.94) / PORTRAIT_CARD_ASPECT,
	);
	const panelWidth = (baseCardWidth * DESIGN_PANEL_WIDTH) / DESIGN_CARD_WIDTH;
	const panelHeight = (PANEL_DESIGN_HEIGHT * panelWidth) / DESIGN_PANEL_WIDTH;
	const homeTabTop =
		safeAreaHeight -
		safeAreaWidth * (FOOTER_BOTTOM_GAP / DESIGN_FRAME_WIDTH) -
		getNavRowHeight(safeAreaWidth, safeAreaHeight, true);
	// Card, dots and panel have to fit between the header bar and the Home tab; on a short phone
	// the card gives up what they don't.
	const frameScale = safeAreaWidth / DESIGN_FRAME_WIDTH;
	const columnRoom =
		homeTabTop -
		PANEL_HOME_GAP * frameScale -
		(insets.top +
			headerTopOffset +
			HEADER_BAR.top +
			HEADER_BAR.height +
			PANEL_HOME_GAP * frameScale);
	const fitCardWidth =
		(columnRoom - panelHeight) /
		(PORTRAIT_CARD_ASPECT + (NAV_TOP_GAP + NAV_BLOCK_HEIGHT + PANEL_TOP_GAP) / DESIGN_CARD_WIDTH);
	const wheelCardWidth = Math.round(
		Math.min(
			safeAreaWidth * (DESIGN_CARD_WIDTH / DESIGN_FRAME_WIDTH),
			(wheelHeight * 0.94) / PORTRAIT_CARD_ASPECT,
			fitCardWidth,
		),
	);
	const wheelCardHeight = Math.round(wheelCardWidth * PORTRAIT_CARD_ASPECT);
	const navScale = wheelCardWidth / DESIGN_CARD_WIDTH;
	const navOffset = NAV_TOP_GAP * navScale - (wheelHeight - wheelCardHeight) / 2;
	// The column is centred in a host that is itself centred, so where the panel ends on screen
	// depends on everything around it. Measure it in the window - where the footer's Home tab is
	// laid out too - and lift the whole column by however far it runs into the tab. The lift is a
	// translate, which the measurement sees, so it is added back to get the unlifted bottom. The
	// measurement only lands after the first paint, so the column stays invisible until then rather
	// than showing unlifted for a frame and jumping up.
	const panelRef = useRef<View>(null);
	const columnLiftRef = useRef(0);
	const [panelBottom, setPanelBottom] = useState(0);
	const columnLift = Math.max(0, panelBottom - (homeTabTop - PANEL_HOME_GAP * frameScale));
	columnLiftRef.current = columnLift;
	const measurePanel = useCallback(() => {
		panelRef.current?.measureInWindow((_x, y, _width, height) =>
			setPanelBottom(y + height + columnLiftRef.current),
		);
	}, []);

	const carouselItems = useMemo(
		() => presentation.map(item => ({ id: item.id, sng: item.sng })),
		[presentation],
	);

	const handleActiveIndexChange = useCallback((index: number) => {
		setActiveIndex(index);
	}, []);

	const handleNavStep = useCallback((direction: -1 | 1) => {
		if (direction === 1) carouselRef.current?.next();
		else carouselRef.current?.prev();
	}, []);

	const handleDotPress = useCallback((index: number) => {
		carouselRef.current?.scrollToIndex(index);
	}, []);

	useEffect(() => {
		if (!presentation.length) return;
		setActiveIndex(current => clamp(current, 0, presentation.length - 1));
	}, [presentation.length]);

	// The desktop frame follows the VIP rooms' two columns: the tournaments on a coverflow over the
	// recent-tables bar on the left, and the registered panel over the jackpot on the right.
	if (isDesktop) {
		const d = desktop.s;
		const columns = getDesktopColumns(desktop.contentWidth, desktop.scale);
		const blockTop = Math.max(0, (desktop.contentHeight - d(DESKTOP_BLOCK_HEIGHT)) / 2);
		const cardHeight = d(DESKTOP_CARD_HEIGHT);
		const cardWidth = cardHeight / PORTRAIT_CARD_ASPECT;

		return (
			<>
				<View
					pointerEvents='box-none'
					style={[styles.desktopRoot, { paddingHorizontal: desktop.side, paddingTop: blockTop }]}
				>
					<View
						pointerEvents='box-none'
						style={[
							styles.desktopColumns,
							{ columnGap: columns.gap, height: d(DESKTOP_BLOCK_HEIGHT) },
						]}
					>
						<View style={{ width: columns.left }}>
							<View style={{ marginTop: d(DESKTOP_TOP_GAP), height: cardHeight }}>
								{loading || presentation.length === 0 ? (
									<View style={styles.tournamentsLoadingWrap}>
										{loading || !initialLoadDone ? (
											<ActivityIndicator size='large' color='#B7C1D1' />
										) : (
											<Text style={[styles.desktopEmpty, { fontSize: d(20) }]}>
												{t('T_SITE_TOURNAMENT_NO_AVAILABLE')}
											</Text>
										)}
									</View>
								) : (
									<DesktopCoverflow<{ id: string; sng: SnG }>
										items={carouselItems}
										keyOf={item => item.id}
										width={columns.left}
										cardWidth={cardWidth}
										cardHeight={cardHeight}
										scale={desktop.scale}
										initialIndex={Math.floor((carouselItems.length - 1) / 2)}
										renderItem={item => (
											<TournamentSlide
												sng={item.sng}
												cardWidth={cardWidth}
												onOpen={openTournament}
											/>
										)}
									/>
								)}
							</View>
							<View style={[styles.desktopBar, { top: d(DESKTOP_BARS_TOP) }]}>
								<DesktopRecentTablesBar
									width={columns.left}
									scale={desktop.scale}
									onPress={() => navigation.navigate(isLogged ? 'MyTables' : 'Login')}
								/>
							</View>
						</View>

						<View style={{ width: columns.right }}>
							<View style={{ marginTop: d(DESKTOP_TOP_GAP) }}>
								<RegisteredTournamentsPanel
									width={columns.right}
									title={t('T_SITE_TOURNAMENT_MY_REGISTERED', {
										defaultValue: 'MY REGISTERED TOURNAMENTS',
									})}
									emptyLabel={t('T_SITE_TOURNAMENT_NONE_REGISTERED', {
										defaultValue: 'No registered tournaments yet.',
									})}
									tournaments={registeredTournaments}
									titleOf={sng => tournamentTitle(sng, t)}
									unregisterLabel={t('T_SITE_TOURNAMENT_UNREGISTER').toUpperCase()}
									onOpen={openTournament}
									onUnregister={unregisterTournament}
									height={cardHeight}
								/>
							</View>
							<View style={[styles.desktopBar, { top: d(DESKTOP_BARS_TOP) }]}>
								<DesktopJackpotBar width={columns.right} scale={desktop.scale} />
							</View>
						</View>
					</View>
				</View>

				{infoTournament && (
					<TournamentInfoModal sng={infoTournament} onClose={() => setInfoTournamentId(null)} />
				)}
			</>
		);
	}

	return (
		<View
			style={[
				styles.tournamentsRoot,
				// The section is centred in what is left under the header. On mobile web the header
				// sits lower (useHeaderTopOffset); padding by that much moves the wheel down with it,
				// the same distance under the top bar as on native.
				{ width: safeAreaWidth, height: safeAreaHeight, paddingTop: headerTopOffset },
			]}
		>
			{loading || presentation.length === 0 ? (
				loading || !initialLoadDone ? (
					<View style={styles.tournamentsLoadingWrap}>
						<ActivityIndicator size='large' color='#B7C1D1' />
					</View>
				) : (
					<Text style={styles.tournamentsEmpty}>{t('T_SITE_TOURNAMENT_NO_AVAILABLE')}</Text>
				)
			) : (
				<View
					style={[
						styles.wheelRoot,
						{
							width: safeAreaWidth,
							height: sectionHeight,
							transform: [{ translateY: -columnLift }],
							opacity: panelBottom === 0 ? 0 : 1,
						},
					]}
				>
					<TournamentsCarousel
						ref={carouselRef}
						items={carouselItems}
						activeIndex={activeIndex}
						onActiveIndexChange={handleActiveIndexChange}
						width={safeAreaWidth}
						height={wheelHeight}
						cardWidth={wheelCardWidth}
						cardHeight={wheelCardHeight}
						progress={carouselProgress}
						onOpenTournament={openTournament}
					/>
					{presentation.length > 1 && (
						<View style={[styles.navWrap, { marginTop: navOffset }]}>
							<CarouselNav
								count={presentation.length}
								activeIndex={activeIndex}
								progress={carouselProgress}
								onDotPress={handleDotPress}
								onStep={handleNavStep}
								scale={navScale}
							/>
						</View>
					)}

					<View
						style={{
							marginTop:
								PANEL_TOP_GAP * navScale + (presentation.length > 1 ? 0 : navOffset + navHeight),
						}}
						ref={panelRef}
						onLayout={measurePanel}
					>
						<RegisteredTournamentsPanel
							width={panelWidth}
							title={t('T_SITE_TOURNAMENT_MY_REGISTERED', {
								defaultValue: 'MY REGISTERED TOURNAMENTS',
							})}
							emptyLabel={t('T_SITE_TOURNAMENT_NONE_REGISTERED', {
								defaultValue: 'No registered tournaments yet.',
							})}
							tournaments={registeredTournaments}
							titleOf={sng => tournamentTitle(sng, t)}
							unregisterLabel={t('T_SITE_TOURNAMENT_UNREGISTER').toUpperCase()}
							onOpen={openTournament}
							onUnregister={unregisterTournament}
							height={panelHeight}
						/>
					</View>
				</View>
			)}

			{infoTournament && (
				<TournamentInfoModal sng={infoTournament} onClose={() => setInfoTournamentId(null)} />
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	tournamentsRoot: {
		position: 'relative',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: 'transparent',
		overflow: 'visible',
	},
	wheelRoot: {
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'visible',
	},
	navWrap: {
		alignItems: 'center',
	},
	desktopRoot: {
		...StyleSheet.absoluteFillObject,
	},
	desktopColumns: {
		flexDirection: 'row',
	},
	desktopBar: {
		position: 'absolute',
		left: 0,
	},
	desktopEmpty: {
		color: '#FFFFFF',
		fontFamily: 'Sofia Sans',
		fontWeight: '700',
	},
	tournamentsLoadingWrap: {
		width: '100%',
		height: '100%',
		alignItems: 'center',
		justifyContent: 'center',
	},
	tournamentsEmpty: {
		color: '#fff',
	},
});

export default TournamentsSection;
